import { useEffect, useMemo } from "react";
import { Outlet, useParams, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGS, type Lang, applyLangToHtml } from "@/i18n";

const isLang = (s: string | undefined): s is Lang =>
  !!s && (SUPPORTED_LANGS as readonly string[]).includes(s);

/** Wraps routes under /:lang. Syncs i18n with URL and html lang/dir. */
export const LangLayout = () => {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLang(lang)) return;
    if (i18n.language !== lang) i18n.changeLanguage(lang);
    applyLangToHtml(lang);
    try { localStorage.setItem("i18nextLng", lang); } catch { /* ignore */ }
  }, [lang, i18n]);

  if (!isLang(lang)) {
    // Redirect to default language preserving the rest of the path
    const stored = (typeof window !== "undefined" && localStorage.getItem("i18nextLng")) || "he";
    const target = isLang(stored) ? stored : "he";
    return <Navigate to={`/${target}${location.pathname}`} replace />;
  }

  return <Outlet />;
};

/** Helper to build a path that preserves the current lang prefix. */
export const useLangPath = () => {
  const { i18n } = useTranslation();
  return useMemo(() => {
    const lang = (isLang(i18n.language) ? i18n.language : "he") as Lang;
    return (path: string) => {
      const clean = path.startsWith("/") ? path : `/${path}`;
      return `/${lang}${clean === "/" ? "" : clean}`;
    };
  }, [i18n.language]);
};
